import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Modal,
  IconButton,
  Grid
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import { YamlService } from '../services/YamlService';
import { WorkflowItemsConfiguration, WorkflowItem } from '../types/WorkflowItemsTypes';
import './WorkflowItems.css';

const WorkflowItems: React.FC = () => {
  const [workflowItems, setWorkflowItems] = useState<WorkflowItemsConfiguration | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<WorkflowItem | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const loadWorkflowItems = async () => {
      try {
        setLoading(true);
        const data = await YamlService.loadYamlFile('_workflow_items.yaml');
        setWorkflowItems(data);
      } catch (err) {
        console.error('Error loading workflow items:', err);
        setError('Failed to load workflow items configuration');
      } finally {
        setLoading(false);
      }
    };

    loadWorkflowItems();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!workflowItems) {
    return (
      <Box p={2}>
        <Alert severity="info">No workflow items configuration found.</Alert>
      </Box>
    );
  }

  const handleItemClick = (item: WorkflowItem) => {
    setSelectedItem(item);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Get the appropriate icon based on the step type
  const getStepIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      'document': '📄',
      'milestone': '🏁',
      'submission': '📤',
      'presentation': '🎯',
      'contract': '📝',
      'signature': '✍️',
      'offer': '💼',
      'notification': '🔔',
      'review': '👁️',
      'endorsement': '👍',
      'document_preparation': '📋',
      'application_review': '🔍',
      'project_setup': '🏗️',
      'questionnaire': '📨',
      'completion': '✅',
      'contract_generation': '📜',
      'contract_signing': '✒️',
      'meeting': '👥',
      'evaluation': '⭐'
    };
    
    return iconMap[type] || '📄';
  };

  // Get CSS class based on type
  const getTypeClass = (type: string) => {
    return `type-${type.toLowerCase().replace('_', '-')}`;
  };

  const renderItemDetailsModal = () => {
    if (!selectedItem) return null;
    
    return (
      <Modal 
        open={modalOpen} 
        onClose={handleCloseModal}
        aria-labelledby="item-details-modal-title"
      >
        <Box className="item-details-modal">
          <Box className="item-details-modal-header">
            <Box display="flex" alignItems="center">
              <Typography id="item-details-modal-title" variant="h6" component="h2" sx={{ ml: 1 }}>
                {selectedItem.title || selectedItem.id}
              </Typography>
              <Chip 
                label={selectedItem.type} 
                color="primary" 
                size="small"
                className={`item-type-chip ${getTypeClass(selectedItem.type)}`}
                sx={{ ml: 1 }}
              />
            </Box>
            <IconButton 
              aria-label="close" 
              onClick={handleCloseModal}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" paragraph>
            {selectedItem.description}
          </Typography>
          
          <Typography variant="subtitle1" gutterBottom>
            Configuration Options:
          </Typography>
          
          <Paper elevation={0} className="item-details-content">
            <List dense>
              {selectedItem.config_options.map((option, index) => (
                <React.Fragment key={option.field}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1" fontWeight="bold" component="span">
                            {option.field}
                          </Typography>
                          {option.required && (
                            <Chip
                              label="Required"
                              size="small"
                              color="secondary"
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box mt={1}>
                          <Typography variant="body2" component="span">
                            Type: {option.type}
                            {option.unit && ` (${option.unit})`}
                          </Typography>
                          
                          {option.default !== undefined && (
                            <Box component="div" mt={0.5}>
                              <Typography variant="body2" component="span">
                                Default: {JSON.stringify(option.default)}
                              </Typography>
                            </Box>
                          )}
                          
                          {(option.allowed_values || option.options) && (
                            <Box component="div" mt={0.5}>
                              <Typography variant="body2" component="span">
                                Allowed values: {JSON.stringify(option.allowed_values || option.options)}
                              </Typography>
                            </Box>
                          )}
                          
                          {option.items && (
                            <Box component="div" mt={0.5}>
                              <Typography variant="body2" component="span">
                                Items: {option.items}
                              </Typography>
                            </Box>
                          )}
                          
                          {option.required_if && (
                            <Box component="div" mt={0.5}>
                              <Typography variant="body2" component="span">
                                Required if: {option.required_if}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      </Modal>
    );
  };

  return (
    <Box className="workflow-items-container">
      <Typography variant="h4" component="h1" gutterBottom>
        {workflowItems.name}
      </Typography>
      <Typography variant="body1" paragraph>
        {workflowItems.description}
      </Typography>

      <Typography variant="h5" component="h2" gutterBottom>
        Available Workflow Items
      </Typography>

      <Box className="workflow-items-grid-container">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {workflowItems.workflow_items.map((item) => (
            <Box 
              key={item.id}
              sx={{ 
                flexBasis: { xs: '100%', sm: 'calc(50% - 16px)', md: 'calc(33.333% - 16px)' },
                flexGrow: 0,
                flexShrink: 0
              }}
            >
              <Paper 
                className={`workflow-item-card ${getTypeClass(item.type)}`}
                onClick={() => handleItemClick(item)}
              >
                <Box className="workflow-item-header">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* <span className="item-icon">{getStepIcon(item.type)}</span> */}
                    <Typography variant="h6" component="div" className="workflow-item-title" sx={{ fontSize: '1.2rem' }}>
                      {item.title || item.id}
                    </Typography>
                  </Box>
                  <Chip 
                    label={item.type} 
                    color="primary" 
                    size="small"
                    className={`item-type-chip ${getTypeClass(item.type)}`}
                    sx={{ 
                      ml: 1
                    }}
                  />
                </Box>
                <Box className="workflow-item-description">
                  <Typography variant="body2">
                    {item.description}
                  </Typography>
                  <Box className="workflow-item-info">
                    <Chip
                      icon={<InfoIcon />}
                      label={`${item.config_options.length} options`}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                    <Button 
                      size="small" 
                      variant="text"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(item);
                      }}
                      className="details-button"
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      </Box>

      {workflowItems.metadata && (
        <Box mt={4} className="metadata-section">
          <Typography variant="h5" component="h2" gutterBottom>
            Metadata
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {workflowItems.metadata.field_types && (
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
                <Card className="metadata-card" elevation={2} sx={{ height: '100%', borderRadius: '8px' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      fontWeight: 600, 
                      pb: 1, 
                      borderBottom: '2px solid #f0f0f0',
                      mb: 2
                    }}>
                      Field Types
                    </Typography>
                    <Box className="metadata-content">
                      {workflowItems.metadata.field_types.map((fieldType) => (
                        <Box 
                          key={fieldType.id} 
                          className="metadata-item" 
                          sx={{ 
                            mb: 1.5, 
                            pb: 1.5, 
                            borderBottom: '1px dashed #eaeaea',
                            '&:last-child': { 
                              mb: 0, 
                              pb: 0, 
                              borderBottom: 'none' 
                            } 
                          }}
                        >
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#2c3e50', 
                              display: 'inline-block',
                              backgroundColor: '#f5f8fa',
                              px: 1,
                              py: 0.5,
                              borderRadius: '4px',
                              mb: 0.5
                            }}
                          >
                            {fieldType.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>Validation:</Box> 
                            <Box component="span" sx={{ 
                              fontFamily: 'monospace', 
                              backgroundColor: '#f8f9fa', 
                              p: 0.5, 
                              borderRadius: '3px',
                              ml: 0.5
                            }}>
                              {fieldType.validation}
                            </Box>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {workflowItems.metadata.status_options && (
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
                <Card className="metadata-card" elevation={2} sx={{ height: '100%', borderRadius: '8px' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      fontWeight: 600, 
                      pb: 1, 
                      borderBottom: '2px solid #f0f0f0',
                      mb: 2
                    }}>
                      Status Options
                    </Typography>
                    <Box className="metadata-content">
                      {workflowItems.metadata.status_options.map((option) => (
                        <Box 
                          key={option.id} 
                          className="metadata-item" 
                          sx={{ 
                            mb: 1.5, 
                            pb: 1.5, 
                            borderBottom: '1px dashed #eaeaea',
                            '&:last-child': { 
                              mb: 0, 
                              pb: 0, 
                              borderBottom: 'none' 
                            } 
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              component="span" 
                              className={`status-indicator status-${option.id}`} 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                mr: 1, 
                                boxShadow: '0 0 0 2px rgba(255,255,255,0.8)'
                              }}
                            ></Box>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: '#2c3e50', 
                                display: 'inline-block',
                                backgroundColor: '#f5f8fa',
                                px: 1,
                                py: 0.5,
                                borderRadius: '4px'
                              }}
                            >
                              {option.id}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {option.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {workflowItems.metadata.touchpoints && (
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
                <Card className="metadata-card" elevation={2} sx={{ height: '100%', borderRadius: '8px' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      fontWeight: 600, 
                      pb: 1, 
                      borderBottom: '2px solid #f0f0f0',
                      mb: 2
                    }}>
                      Touchpoints
                    </Typography>
                    <Box className="metadata-content">
                      {workflowItems.metadata.touchpoints.map((touchpoint) => (
                        <Box 
                          key={touchpoint.id} 
                          className="metadata-item" 
                          sx={{ 
                            mb: 1.5, 
                            pb: 1.5, 
                            borderBottom: '1px dashed #eaeaea',
                            '&:last-child': { 
                              mb: 0, 
                              pb: 0, 
                              borderBottom: 'none' 
                            } 
                          }}
                        >
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#2c3e50', 
                              display: 'inline-block',
                              backgroundColor: '#f5f8fa',
                              px: 1,
                              py: 0.5,
                              borderRadius: '4px',
                              mb: 0.5
                            }}
                          >
                            {touchpoint.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {touchpoint.description}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
            
            {workflowItems.metadata.reminder_types && (
              <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' } }}>
                <Card className="metadata-card" elevation={2} sx={{ height: '100%', borderRadius: '8px' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" gutterBottom sx={{ 
                      fontWeight: 600, 
                      pb: 1, 
                      borderBottom: '2px solid #f0f0f0',
                      mb: 2
                    }}>
                      Reminder Types
                    </Typography>
                    <Box className="metadata-content">
                      {workflowItems.metadata.reminder_types.map((reminderType) => (
                        <Box 
                          key={reminderType.id} 
                          className="metadata-item" 
                          sx={{ 
                            mb: 1.5, 
                            pb: 1.5, 
                            borderBottom: '1px dashed #eaeaea',
                            '&:last-child': { 
                              mb: 0, 
                              pb: 0, 
                              borderBottom: 'none' 
                            } 
                          }}
                        >
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: '#2c3e50', 
                              display: 'inline-block',
                              backgroundColor: '#f5f8fa',
                              px: 1,
                              py: 0.5,
                              borderRadius: '4px',
                              mb: 0.5
                            }}
                          >
                            {reminderType.id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <Box component="span" sx={{ fontWeight: 500, color: '#64748b' }}>Days before:</Box> 
                            {reminderType.config.days_before.map((day, index) => (
                              <Chip 
                                key={index}
                                label={day}
                                size="small"
                                sx={{ 
                                  height: 20, 
                                  ml: 1, 
                                  backgroundColor: '#e8f4fc', 
                                  color: '#2563eb', 
                                  fontWeight: 600,
                                  fontSize: '0.7rem' 
                                }}
                              />
                            ))}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        </Box>
      )}
      
      {renderItemDetailsModal()}
    </Box>
  );
};

export default WorkflowItems; 